// src/sectors/pilates/sessions/components/SessionDetailDialog/SessionDetailAtoms.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

export function toneColor(theme, tone) {
  if (tone === 'blue') return theme.palette.info.main;
  if (tone === 'red') return theme.palette.error.main;
  return theme.palette.success.main; // mint
}

export function SoftCard({ children, tone = 'mint', sx }) {
  return (
    <MDBox
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark';
        const c = toneColor(theme, tone);
        const border = `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`;
        return {
          borderRadius: '20px',
          border,
          background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.92)',
          boxShadow: `0 18px 44px ${alpha(c, isDark ? 0.12 : 0.09)}`,
          p: 1.2,
          display: 'grid',
          gap: 1,
          overflow: 'hidden',
          ...(typeof sx === 'function' ? sx(theme) : sx),
        };
      }}
    >
      {children}
    </MDBox>
  );
}

export function MiniTag({ icon, label, tone = 'blue' }) {
  return (
    <MDBox
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark';
        const c = toneColor(theme, tone);
        const border = `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`;
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 0.95,
          py: 0.55,
          borderRadius: '14px',
          border,
          background: alpha(c, isDark ? 0.14 : 0.1),
          fontWeight: 900,
          minWidth: 0,
        };
      }}
    >
      <Icon sx={(t) => ({ fontSize: 18, color: toneColor(t, tone) })}>{icon}</Icon>
      <MDTypography variant="caption" sx={{ fontWeight: 900, lineHeight: 1 }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}

export function PillButton({ icon, title, sub, tone = 'mint', onClick, right }) {
  return (
    <MDBox
      onClick={onClick}
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark';
        const c = toneColor(theme, tone);
        const border = `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`;
        const bg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.92)';
        return {
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: '18px',
          border,
          background: bg,
          boxShadow: `0 12px 28px ${alpha('#111827', isDark ? 0.22 : 0.08)}`,
          p: 1.1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          transition: 'transform .14s ease, box-shadow .14s ease, background .14s ease',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 16px 34px ${alpha(c, 0.16)}` },
          '&:active': { transform: 'scale(0.995)' },
          minWidth: 0,
        };
      }}
    >
      <MDBox
        sx={(theme) => {
          const isDark = theme.palette.mode === 'dark';
          const border = `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1)}`;
          return {
            width: 36,
            height: 36,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            border,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.90)',
            flex: '0 0 auto',
          };
        }}
      >
        <Icon sx={(t) => ({ color: toneColor(t, tone), fontSize: 18 })}>{icon}</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.1 }}>
        <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
          {title}
        </MDTypography>
        {sub ? (
          <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
            {sub}
          </MDTypography>
        ) : null}
      </MDBox>

      <MDBox sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.6 }}>
        {right}
        <Icon sx={{ fontSize: 18, opacity: 0.65 }}>chevron_right</Icon>
      </MDBox>
    </MDBox>
  );
}

export function StatPill({ label, value, tone }) {
  return (
    <MDBox
      sx={(t) => {
        const isD = t.palette.mode === 'dark';
        const c = toneColor(t, tone);
        const border = `1px solid ${alpha(t.palette.text.primary, isD ? 0.14 : 0.1)}`;
        return {
          px: 1,
          py: 0.55,
          borderRadius: '14px',
          border,
          background: alpha(c, isD ? 0.14 : 0.1),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          fontWeight: 900,
        };
      }}
    >
      <MDTypography variant="caption" sx={{ fontWeight: 900, opacity: 0.85 }}>
        {label}
      </MDTypography>
      <MDTypography variant="caption" sx={{ fontWeight: 1000 }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}
